import React from 'react';
import {
  Calculator,
  Image as ImageIcon,
  MapPin,
  Package,
  TrendingDown,
  TrendingUp,
  Truck,
  Zap,
} from 'lucide-react';

function FinancialSummary({
  financials,
  paymentImages = [],
  onShowImages,
  expressDeliveries = [],
}) {
  const {
    totalCollected,
    totalToDeposit,
    totalDeliveryCount,
    deliveryCount,
    deliveryCountProvince,
    totalServiceFee,
    totalExchange,
    totalProvinciaCosts,
    dropshipDeliveries,
    dropshipTotalCost,
    limaCollected,
    limaServiceFee,
    limaDropshipCost,
    limaToDeposit,
    provinciaCollected,
    provinciaServiceFee,
    provinciaTransportCost,
    provinciaDropshipCost,
    provinciaToDeposit,
  } = financials;

  const expressCount = expressDeliveries.length;
  const expressTotalPrice = expressDeliveries.reduce(
    (sum, delivery) => sum + (parseFloat(delivery.total_price) || 0),
    0
  );
  const hasImages = paymentImages && paymentImages.length > 0;
  const totalCosts = totalServiceFee + totalProvinciaCosts + dropshipTotalCost;

  return (
    <div className="space-y-4">
      <section className="bg-gradient-to-br from-[#B1336E] to-[#E6B43C] rounded-xl shadow-md p-4 text-white">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-extrabold font-montserrat">
              Resumen Financiero
            </h2>
            <p className="text-xs text-white/80 font-montserrat">
              Analisis del dia seleccionado
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              if (hasImages && onShowImages) onShowImages();
            }}
            disabled={!hasImages}
            className={`relative h-10 w-10 shrink-0 rounded-lg bg-white/20 flex items-center justify-center transition-all group ${
              hasImages ? 'hover:bg-white/30 cursor-pointer' : 'opacity-50 cursor-default'
            }`}
            aria-label={hasImages ? 'Ver comprobantes de deposito' : 'Sin comprobantes de deposito'}
          >
            <ImageIcon className="w-5 h-5" />
            {hasImages && (
              <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 bg-green-500 rounded-full flex items-center justify-center shadow border-2 border-white text-[10px] font-bold font-montserrat">
                {paymentImages.length}
              </span>
            )}
            <span className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg font-montserrat shadow-lg w-56 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
              {hasImages
                ? `Ver ${paymentImages.length} comprobante${paymentImages.length !== 1 ? 's' : ''} de deposito`
                : 'No hay comprobantes para esta fecha'}
            </span>
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-2">
          <MetricTile
            icon={<TrendingUp className="w-4 h-4" />}
            label="Recaudado"
            value={`S/ ${totalCollected.toFixed(2)}`}
            helper={totalExchange > 0 ? `Cambio -S/ ${totalExchange.toFixed(2)}` : 'Cobrado + transf.'}
          />
          <MetricTile
            icon={<Calculator className="w-4 h-4" />}
            label="Costos"
            value={`S/ ${totalCosts.toFixed(2)}`}
            helper={`Servicio S/ ${totalServiceFee.toFixed(2)}`}
          />
          <MetricTile
            icon={<TrendingDown className="w-4 h-4" />}
            label="A depositar"
            value={`S/ ${totalToDeposit.toFixed(2)}`}
            helper="Neto total"
          />
          <MetricTile
            icon={<Package className="w-4 h-4" />}
            label="Entregas"
            value={totalDeliveryCount}
            helper={
              [
                deliveryCount > 0 ? `Lima ${deliveryCount}` : null,
                deliveryCountProvince > 0 ? `Prov. ${deliveryCountProvince}` : null,
                dropshipDeliveries > 0 ? `Drop ${dropshipDeliveries}` : null,
              ].filter(Boolean).join(' · ') || 'Cobradas'
            }
          />
        </div>

        <p className="mt-3 border-t border-white/20 pt-3 text-[11px] leading-snug text-white/80 font-montserrat">
          <strong>Nota:</strong> Solo estados "Entregado" o "Rechazado".
          {totalProvinciaCosts > 0 && ' Transporte provincia incluido.'}
          {dropshipTotalCost > 0 && ' Dropshipping descontado.'}
        </p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {deliveryCount > 0 && (
          <BreakdownCard
            accent="#B1336E"
            icon={<Truck className="w-5 h-5" />}
            title="Entregas Lima"
            rows={[
              { label: 'Recaudado', value: `S/ ${limaCollected.toFixed(2)}`, tone: 'positive' },
              { label: 'Servicio Fast Max', value: `- S/ ${limaServiceFee.toFixed(2)}`, tone: 'negative' },
              limaDropshipCost > 0
                ? { label: 'Costo Dropship', value: `- S/ ${limaDropshipCost.toFixed(2)}`, tone: 'negative' }
                : null,
            ]}
            totalLabel="A Depositar Hoy"
            totalValue={`S/ ${limaToDeposit.toFixed(2)}`}
            footer={`${deliveryCount} entrega${deliveryCount !== 1 ? 's' : ''}`}
          />
        )}

        {deliveryCountProvince > 0 && (
          <BreakdownCard
            accent="#E6B43C"
            icon={<MapPin className="w-5 h-5" />}
            title="Entregas Provincia"
            rows={[
              { label: 'Recaudado', value: `S/ ${provinciaCollected.toFixed(2)}`, tone: 'positive' },
              { label: 'Servicio Fast Max', value: `- S/ ${provinciaServiceFee.toFixed(2)}`, tone: 'negative' },
              provinciaTransportCost > 0
                ? { label: 'Costo Transporte', value: `- S/ ${provinciaTransportCost.toFixed(2)}`, tone: 'negative' }
                : null,
              provinciaDropshipCost > 0
                ? { label: 'Costo Dropship', value: `- S/ ${provinciaDropshipCost.toFixed(2)}`, tone: 'negative' }
                : null,
            ]}
            totalLabel="A Depositar Pendiente"
            totalValue={`S/ ${provinciaToDeposit.toFixed(2)}`}
            footer={`${deliveryCountProvince} entrega${deliveryCountProvince !== 1 ? 's' : ''}`}
          />
        )}

        {expressCount > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-4 border border-[#67A43C]/40">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#67A43C]/10 rounded-lg flex items-center justify-center text-[#67A43C]">
                <Zap className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-extrabold text-[#67A43C] font-montserrat">
                  Entregas Express
                </h3>
                <p className="text-xs text-slate-500 font-montserrat">Solo Lima · Por kilometro</p>
              </div>
            </div>

            <div className="flex items-center justify-between border-b border-slate-100 py-2">
              <span className="text-sm font-medium text-slate-700 font-montserrat">
                Servicios del dia
              </span>
              <span className="text-lg font-bold text-slate-900 font-montserrat">
                {expressCount}
              </span>
            </div>

            <div className="mt-2 max-h-28 overflow-y-auto space-y-1.5">
              {expressDeliveries.map((delivery) => (
                <div key={delivery.id} className="flex items-center justify-between text-xs font-montserrat text-slate-500">
                  <div className="min-w-0 flex items-center gap-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-white text-[10px] font-semibold ${delivery.vehicle_type === 'bike' ? 'bg-[#67A43C]' : 'bg-[#B1336E]'}`}>
                      {delivery.vehicle_type === 'bike' ? 'Moto' : 'Auto'}
                    </span>
                    <span className="truncate max-w-[120px]">{delivery.recipient_name}</span>
                    <span>{delivery.distance_km} km</span>
                  </div>
                  <span className="font-semibold text-slate-700">
                    S/ {Number(delivery.total_price).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>

            <div className="bg-[#67A43C]/5 rounded-lg p-3 mt-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-extrabold text-[#67A43C] font-montserrat">
                  Total Express
                </span>
                <span className="text-xl font-extrabold text-[#67A43C] font-montserrat">
                  S/ {expressTotalPrice.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricTile({ icon, label, value, helper }) {
  return (
    <div className="rounded-lg border border-white/20 bg-white/10 px-3 py-2.5 backdrop-blur-sm min-w-0">
      <div className="flex items-center gap-1.5 text-white/90">
        {icon}
        <p className="truncate text-[11px] font-semibold font-montserrat">{label}</p>
      </div>
      <p className="mt-1 truncate text-xl font-extrabold leading-tight font-montserrat">
        {value}
      </p>
      <p className="mt-0.5 truncate text-[10px] text-white/70 font-montserrat">
        {helper}
      </p>
    </div>
  );
}

function BreakdownCard({ accent, icon, title, rows, totalLabel, totalValue, footer }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 border" style={{ borderColor: `${accent}66` }}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}1A`, color: accent }}>
          {icon}
        </div>
        <h3 className="text-base font-extrabold font-montserrat" style={{ color: accent }}>
          {title}
        </h3>
      </div>

      <div className="space-y-2">
        {rows.filter(Boolean).map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2">
            <span className="text-sm font-medium text-slate-700 font-montserrat">
              {row.label}
            </span>
            <span className={`text-sm font-bold font-montserrat ${row.tone === 'negative' ? 'text-red-600' : 'text-slate-900'}`}>
              {row.value}
            </span>
          </div>
        ))}

        <div className="rounded-lg p-3" style={{ backgroundColor: `${accent}0D` }}>
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-extrabold font-montserrat" style={{ color: accent }}>
              {totalLabel}
            </span>
            <span className="text-xl font-extrabold font-montserrat" style={{ color: accent }}>
              {totalValue}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1 font-montserrat">{footer}</p>
        </div>
      </div>
    </div>
  );
}

export default FinancialSummary;
